import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Spinner, InputGroup, Button, Form } from 'react-bootstrap';
import { Card, CardContent, CardMedia } from '@mui/material';
import '../../styles/CardProductos.css';
import axios from 'axios';
import { useCarrito } from './CarritoContext';
import { API } from '../../../../api/URL_API';
import { Cart2, DashLg, PlusLg } from 'react-bootstrap-icons';
import FloatingButton from '../../FloatingButton';

function ProductosUni({ idCategoria }) {
    const [products, setProducts] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(12);
    const [totalProducts, setTotalProducts] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const loaderRef = useRef(null);
    const { addToCarrito, selectedCategories } = useCarrito();

    useEffect(() => {
        window.scrollTo(0, 0);
        return () => {
            // Limpiar observador al desmontar
            if (loaderRef.current && observer.current) {
                observer.current.unobserve(loaderRef.current);
            }
        };
    }, []);

    const observer = useRef();

    const formatCurrency = (amount) => {
        const numberAmount = typeof amount === 'string'
            ? parseFloat(amount.replace(/[^0-9,-]+/g, ''))
            : amount;
        return numberAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    const fetchProducts = useCallback(async (page = 1, isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setIsLoading(true);
                setProducts([]);
                setQuantities({});
                setHasMore(true); // Reiniciar paginación al inicio
            } else {
                setIsLoadingMore(true);
            }

            let url;
            let params = {
                limit: productsPerPage,
                offset: (page - 1) * productsPerPage,
                _: Date.now()
            };

            if (selectedCategories.length > 0) {
                url = `${API}/api/mostrarCategorias/productos-por-categorias`;
                params.categoryIds = selectedCategories.join(',');
            } else if (idCategoria) {
                url = `${API}/api/mostrarCategorias/productos/${idCategoria}`;
                params.page = page;
            }

            const response = await axios.get(url, { params, headers: { 'Cache-Control': 'no-cache' } });

            //console.log('Respuesta API página', page, response.data);

            if (response.data) {
                let productsData = [];
                let receivedTotal = 0;

                if (Array.isArray(response.data)) {
                    productsData = response.data;
                    receivedTotal = response.data.length;
                } else if (response.data.products && Array.isArray(response.data.products)) {
                    productsData = response.data.products;
                    receivedTotal = response.data.total || response.data.products.length;
                }

                const processedProducts = productsData.map(product => ({
                    ...product,
                    precioU: typeof product.precioU === 'string' ? parseFloat(product.precioU.replace(/[^0-9,-]+/g, '')) : product.precioU,
                    precioM: typeof product.precioM === 'string' ? parseFloat(product.precioM.replace(/[^0-9,-]+/g, '')) : product.precioM,
                }));

                setProducts(prev => {
                    const existingIds = new Set(prev.map(p => p.idProducto));
                    const newProducts = processedProducts.filter(p => !existingIds.has(p.idProducto));
                    const updatedProducts = [...prev, ...newProducts];

                    setHasMore(updatedProducts.length < receivedTotal);

                    return updatedProducts;
                });

                setTotalProducts(receivedTotal);

                setQuantities(prev => {
                    const newQuantities = { ...prev };
                    processedProducts.forEach(product => {
                        if (!newQuantities[product.idProducto]) {
                            newQuantities[product.idProducto] = 1;
                        }
                    });
                    return newQuantities;
                });

                // Si no recibimos productos nuevos, ya no hay más para cargar
                if (processedProducts.length === 0) {
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Error al cargar productos:', error);
            setHasMore(false);
        } finally {
            if (isInitialLoad) {
                setIsLoading(false);
            } else {
                setIsLoadingMore(false);
            }
        }
    }, [selectedCategories, idCategoria, productsPerPage]);


    useEffect(() => {
        // Resetear estado cuando cambian las categorías o idCategoria
        setCurrentPage(1);
        fetchProducts(1, true);
    }, [fetchProducts]);

    const handleAddToCart = (product) => {
        const quantityToCart = quantities[product.idProducto] || 1;

        addToCarrito({
            idProducto: product.idProducto,
            referencia: product.referencia,
            nomProducto: product.nomProducto,
            precioU: product.precioU,
            precioM: product.precioM,
            image: product.imgProducto || 'URL_DEFAULT_IMAGE',
            categoria: idCategoria,
            idCategoria: product.idCategoria ?? null,
            quantity: quantityToCart,
            stock: product.stock,
        });
    };

    const updateQuantity = (productId, newQuantity) => {
        setQuantities(prev => ({
            ...prev,
            [productId]: Math.max(1, Math.min(newQuantity, products.find(p => p.idProducto === productId)?.stock || 1))
        }));
    };

    // Configuración del IntersectionObserver
    useEffect(() => {
        if (!hasMore || isLoading || isLoadingMore) return;

        const options = {
            root: null,
            rootMargin: '100px',
            threshold: 0
        };

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prev => {
                    const nextPage = prev + 1;
                    fetchProducts(nextPage).then(() => {
                        // Después del fetch, si no hay más, no incrementamos el page
                        if (!hasMore) return prev;
                    });
                    return nextPage;
                });
            }
        }, options);

        if (loaderRef.current) {
            observer.current.observe(loaderRef.current);
        }

        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [hasMore, isLoading, isLoadingMore, fetchProducts]);


    const visibleProducts = products.slice(0, currentPage * productsPerPage);
    const obtenerImagenesVisibles = () => visibleProducts.map(product => product.imgProducto);

    useEffect(() => {
        console.log(`Mostrando ${visibleProducts.length} de ${totalProducts} productos`);
    }, [visibleProducts, totalProducts]);

    return (
        <>
            <Container fluid className='py-4'>
                <div className='d-flex align-items-center justify-content-center w-100 mb-4'>
                    <h4 className='fw-bold text-uppercase text-dark fs-1'>{idCategoria}</h4>
                </div>

                <Row>
                    {isLoading && !isLoadingMore ? (
                        <Col xs={12} className='text-center carga-productos'>
                            <Spinner animation='border' role='status' variant='danger' />
                            <p className='text-dark fw-bold'>Cargando productos...</p>
                        </Col>
                    ) : visibleProducts.length === 0 ? (
                        <Col xs={12} className='text-center carga-productos'>
                            <p className='text1-cr fw-bold fst-italic'>No hay productos disponibles.</p>
                        </Col>
                    ) : (
                        visibleProducts.map((product) => (
                            <Col key={product.idProducto} xs={6} sm={4} md={3} className='d-flex justify-content-center mb-3'>
                                <Card className='position-relative' style={{
                                    boxShadow: '0 4px 8px rgba(43, 35, 94, 0.3), 0 6px 20px rgba(43, 35, 94, 0.3)',
                                    borderRadius: '15px'
                                }}>
                                    <CardMedia
                                        component='img'
                                        image={product.imgProducto}
                                        alt={`product-${product.idProducto}`}
                                        loading='lazy'
                                        height="200"
                                        style={{ objectFit: 'contain' }}
                                    />
                                    <CardContent className='text-center text-uppercase'>
                                        <h4 className='fw-bold'>{product.nomProducto}</h4>
                                        <h5 className='mb-3'>UNIDAD: $ {formatCurrency(product.precioU)}</h5>
                                        <h6 className='mb-3 fs-6 text-muted'>X MAYOR: $ {formatCurrency(product.precioM)}</h6>

                                        <div className="d-flex justify-content-center align-items-center">
                                            <InputGroup className='mt-2' style={{ width: '250px' }}>
                                                <Button
                                                    size='sm'
                                                    variant='danger'
                                                    onClick={() => updateQuantity(product.idProducto, quantities[product.idProducto] - 1)}
                                                    disabled={quantities[product.idProducto] <= 1}
                                                >
                                                    <DashLg size={18} />
                                                </Button>
                                                <Form.Control
                                                    className='text-center'
                                                    type='number'
                                                    min="1"
                                                    max={product.stock}
                                                    value={quantities[product.idProducto] || 1}
                                                    onChange={(e) => {
                                                        let newQuantity = parseInt(e.target.value, 10) || 1;
                                                        newQuantity = Math.max(1, Math.min(newQuantity, product.stock));
                                                        updateQuantity(product.idProducto, newQuantity);
                                                    }}
                                                />
                                                <Button
                                                    size='sm'
                                                    variant='success'
                                                    onClick={() => updateQuantity(product.idProducto, quantities[product.idProducto] + 1)}
                                                    disabled={quantities[product.idProducto] >= product.stock}
                                                >
                                                    <PlusLg size={18} />
                                                </Button>
                                            </InputGroup>
                                        </div>

                                        <Button
                                            className="buy-btn-3d mt-2"
                                            variant="outline-success"
                                            onClick={() => handleAddToCart(product)}
                                            disabled={product.stock <= 0}
                                        >
                                            <Cart2 size={18} className="me-2" />
                                            Comprar
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Col>
                        ))
                    )}
                </Row>

                {isLoadingMore && (
                    <Row className='mt-3'>
                        <Col className='text-center'>
                            <Spinner animation="border" size="sm" />
                            <p>Cargando más productos...</p>
                        </Col>
                    </Row>
                )}

                <div ref={loaderRef} style={{ height: '20px' }} />
            </Container>
            <FloatingButton
                obtenerImagenesVisibles={obtenerImagenesVisibles}
                nomCategoria={idCategoria}
            />
        </>
    );
}

export default ProductosUni;